/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { ICache, RequestCache } from '@msdyn365-commerce/cache-internal';
import {
    IAny,
    IExperimentationConnector,
    IGeneric,
    IRequestContext,
    MSDyn365Commerce,
    msdyn365Commerce,
    skipConsoleWarn
} from '@msdyn365-commerce/core-internal';
import { initializeApp } from './initialization';
import { IBinding, IMSDyn365CommerceWithPrototype } from './models';
import { createComponent } from './render-component';
import { initBindings } from './utils';

interface IConnectors {
    experimentation: IExperimentationConnector;
}

const requestCache: ICache = new RequestCache();

/**
 * Helper method to inject createComponent method from runtime-internal.
 * We add a dummy createComponent method initially to avoid running into circular lookup of
 * ecommerce components
 */
const addCreateComponentHook = () => {
    (<IMSDyn365CommerceWithPrototype>MSDyn365Commerce).prototype.updatePrivateVariable = function(): void {
        // @ts-ignore -- accessing a private class variable which is a valid JS
        this.createComponent = createComponent;
    };
    (<IGeneric<IAny>>msdyn365Commerce).updatePrivateVariable();
};

export const initializeClientApp = async (bindings: IBinding, connectors: IConnectors) => {
    skipConsoleWarn();
    addCreateComponentHook();
    initializeApp(bindings);
    const clientPlatformSettings = (<any>window).___initialData___ && (<any>window).___initialData___._clientPlatformSetting;
    await initBindings(bindings, clientPlatformSettings && clientPlatformSettings.enableModuleEntryPoints);

    (<IMSDyn365CommerceWithPrototype>MSDyn365Commerce).prototype.updatePrivateVariable = function(): void {
        const appDatabv = (<any>window).___appData___ && (<any>window).___appData___.bv;

        // @ts-ignore -- accessing a private class variable which is a valid JS
        this._buildVersion = appDatabv;

        // @ts-ignore -- accessing a private class variable which is a valid JS
        this._experimentationConnector = connectors?.experimentation;

        // @ts-ignore -- accessing a private class variable which is a valid JS
        this._platformSettings = clientPlatformSettings;

        // @ts-ignore -- accessing a private class variable which is a valid JS
        this._versionDependentFeatureInfo = (<any>window).___initialData___ && (<any>window).___initialData___._versionDependentFeatureInfo;

        // @ts-ignore -- accessing a private class variable which is a valid JS
        this._environmentVariable = (<any>window).___initialData___ && (<any>window).___initialData___._environmentVariable;
    };
    (<IGeneric<IAny>>msdyn365Commerce).updatePrivateVariable();

    (<IMSDyn365CommerceWithPrototype>MSDyn365Commerce).prototype.getAppCache = (requestContext: IRequestContext) => requestCache;
};
